import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AccountSettings from './AccountSettings';
import { User, Mail, CreditCard, Shield, ArrowLeft, Calendar, History, ChevronRight, LogOut } from 'lucide-react';
import { BOOK_PATH } from '../lib/routes';

const LOGO = "https://customer-assets.emergentagent.com/job_buraq-transport/artifacts/0a9wh5vw_0E081036-6309-4011-9127-0A9FE497D0F8.png";

const Profile = () => {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const initials = `${currentUser?.firstName?.charAt(0) || ''}${currentUser?.lastName?.charAt(0) || ''}`.toUpperCase();
  const memberSince = currentUser?.createdAt ? new Date(currentUser.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—';

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[520px] h-[520px] lg:w-[800px] lg:h-[800px] rounded-full bg-orange-600/15 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 right-0 w-[360px] h-[360px] rounded-full bg-orange-500/10 blur-[120px]" />

      <div className="relative w-full px-5 sm:px-6 lg:px-10 2xl:px-16 pb-16">
        <header className="flex items-center justify-between py-6 lg:py-8">
          <button
            data-testid="back-btn"
            onClick={() => navigate('/dashboard')}
            className="w-11 h-11 rounded-full border border-white/10 flex items-center justify-center text-gray-300 hover:text-white hover:border-orange-500/40 active:scale-95 transition-all"
            aria-label="Back"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <button
            data-testid="logo-home"
            onClick={() => navigate('/')}
            className="flex items-center gap-2.5 active:scale-95 transition-transform"
            aria-label="Home"
          >
            <img src={LOGO} alt="RideBuraq" className="w-10 h-10 object-contain" />
            <span className="text-2xl font-bold tracking-wide text-white">Ride<span className="bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">Buraq</span></span>
          </button>
        </header>

        <div className="max-w-3xl mx-auto">

        {/* Identity */}
        <div className="flex flex-col items-center text-center pt-6 pb-8">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-3xl font-bold text-white shadow-xl shadow-orange-900/40 mb-4">
            {initials}
          </div>
          <h1 className="text-3xl font-semibold tracking-tight capitalize">{currentUser?.firstName} {currentUser?.lastName}</h1>
          <p className="text-gray-500 text-sm mt-1">{currentUser?.email}</p>
          <div className="inline-flex items-center gap-1.5 mt-4 px-3 py-1 rounded-full border border-emerald-400/20 bg-emerald-400/5">
            <Shield className="h-3 w-3 text-emerald-400" />
            <span className="text-[11px] tracking-[0.15em] uppercase text-emerald-300">Active Member</span>
          </div>
        </div>

        {/* Personal info */}
        <p className="text-[11px] tracking-[0.3em] uppercase text-gray-500 mb-3">Details</p>
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] divide-y divide-white/5 mb-6">
          <div className="flex items-center justify-between px-4 py-3.5">
            <span className="flex items-center gap-2 text-gray-400 text-sm"><User className="h-4 w-4 text-orange-400" />Name</span>
            <span className="text-sm text-white capitalize">{currentUser?.firstName} {currentUser?.lastName}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-3.5">
            <span className="flex items-center gap-2 text-gray-400 text-sm"><Mail className="h-4 w-4 text-orange-400" />Email</span>
            <span className="text-sm text-white truncate max-w-[55%]">{currentUser?.email}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-3.5">
            <span className="flex items-center gap-2 text-gray-400 text-sm"><Calendar className="h-4 w-4 text-orange-400" />Member Since</span>
            <span className="text-sm text-white">{memberSince}</span>
          </div>
        </div>

        <AccountSettings user={currentUser} />

        {/* Actions */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] divide-y divide-white/5 mb-6">
          <button
            data-testid="history-link"
            onClick={() => navigate('/rides')}
            className="w-full flex items-center justify-between px-4 py-4 hover:bg-white/[0.03] transition-colors"
          >
            <span className="flex items-center gap-3 text-sm text-white"><History className="h-4 w-4 text-orange-400" />Ride History</span>
            <ChevronRight className="h-4 w-4 text-gray-600" />
          </button>
          <button
            data-testid="book-link"
            onClick={() => navigate(BOOK_PATH)}
            className="w-full flex items-center justify-between px-4 py-4 hover:bg-white/[0.03] transition-colors"
          >
            <span className="flex items-center gap-3 text-sm text-white"><CreditCard className="h-4 w-4 text-orange-400" />Book a Ride</span>
            <ChevronRight className="h-4 w-4 text-gray-600" />
          </button>
        </div>

        <button
          data-testid="logout-btn"
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 rounded-full py-3.5 border border-red-500/20 text-red-400 hover:bg-red-500/10 active:scale-[0.99] transition-all"
        >
          <LogOut className="h-4 w-4" />
          <span className="text-sm font-medium">Sign Out</span>
        </button>
        </div>
      </div>
    </div>
  );
};

export default Profile;
